// src/utils/documentPlaceholders.js
// Document Placeholder System

export const PLACEHOLDER_CATEGORIES = {
    EMPLOYEE: 'Employee Information',
    COMPANY: 'Company Information',
    DATE: 'Date & Time',
    CUSTOM: 'Custom Fields'
};

export const PLACEHOLDERS = {
    // Employee Information
    EMPLOYEE: [
        { id: 'employee_name', label: 'Employee Name', placeholder: '{{employee_name}}', example: 'John Doe' },
        { id: 'employee_code', label: 'Employee Code', placeholder: '{{employee_code}}', example: 'EMP001' },
        { id: 'employee_email', label: 'Employee Email', placeholder: '{{employee_email}}', example: 'john.doe@company.com' },
        { id: 'employee_phone', label: 'Employee Phone', placeholder: '{{employee_phone}}', example: '+1234567890' },
        { id: 'designation', label: 'Designation/Job Title', placeholder: '{{designation}}', example: 'Software Engineer' },
        { id: 'department', label: 'Department', placeholder: '{{department}}', example: 'Engineering' },
        { id: 'employee_address', label: 'Employee Address', placeholder: '{{employee_address}}', example: '123 Main St, City' },
        { id: 'join_date', label: 'Joining Date', placeholder: '{{join_date}}', example: '01-Jan-2024' },
        { id: 'salary', label: 'Salary', placeholder: '{{salary}}', example: '₹50,000' },
        { id: 'manager_name', label: 'Manager Name', placeholder: '{{manager_name}}', example: 'Jane Smith' },
        { id: 'location', label: 'Work Location', placeholder: '{{location}}', example: 'New York Office' },
        { id: 'probation_months', label: 'Probation Period (Months)', placeholder: '{{probation_months}}', example: '3' },
        { id: 'notice_period', label: 'Notice Period (Days)', placeholder: '{{notice_period}}', example: '30' }
    ],

    // Company Information
    COMPANY: [
        { id: 'company_logo', label: 'Company Logo', placeholder: '{{company_logo}}', example: '[Company Logo Image]' },
        { id: 'company_name', label: 'Company Name', placeholder: '{{company_name}}', example: 'ABC Corporation' },
        { id: 'company_address', label: 'Company Address', placeholder: '{{company_address}}', example: '456 Business Blvd' },
        { id: 'company_email', label: 'Company Email', placeholder: '{{company_email}}', example: 'hr@company.com' },
        { id: 'company_phone', label: 'Company Phone', placeholder: '{{company_phone}}', example: '+1234567890' },
        { id: 'company_website', label: 'Company Website', placeholder: '{{company_website}}', example: 'www.company.com' },
        { id: 'hr_name', label: 'HR Name', placeholder: '{{hr_name}}', example: 'Sarah Johnson' },
        { id: 'hr_title', label: 'HR Title', placeholder: '{{hr_title}}', example: 'HR Manager' },
        { id: 'ceo_name', label: 'CEO Name', placeholder: '{{ceo_name}}', example: 'Michael Brown' }
    ],

    // Date & Time
    DATE: [
        { id: 'current_date', label: 'Current Date', placeholder: '{{current_date}}', example: '15-Nov-2024' },
        { id: 'current_month', label: 'Current Month', placeholder: '{{current_month}}', example: 'November' },
        { id: 'current_year', label: 'Current Year', placeholder: '{{current_year}}', example: '2024' },
        { id: 'current_day', label: 'Current Day', placeholder: '{{current_day}}', example: 'Friday' }
    ]
};

/**
 * Replace placeholders in content with actual data
 * @param {string} content - The content with placeholders
 * @param {object} employeeData - Employee data object
 * @param {object} companyData - Company data object
 * @returns {string} - Content with placeholders replaced
 */
export const replacePlaceholders = (content, employeeData = {}, companyData = {}) => {
    if (!content) return '';

    let processedContent = content;

    // Replace employee placeholders
    processedContent = processedContent
        .replace(/{{employee_name}}/g, employeeData.name || employeeData.full_name || '[Employee Name]')
        .replace(/{{employee_code}}/g, employeeData.employee_code || employeeData.code || '[Employee Code]')
        .replace(/{{employee_email}}/g, employeeData.email || '[Employee Email]')
        .replace(/{{employee_phone}}/g, employeeData.phone || employeeData.contact_number || '[Phone Number]')
        .replace(/{{designation}}/g, employeeData.designation || employeeData.job_title || '[Designation]')
        .replace(/{{department}}/g, employeeData.department || '[Department]')
        .replace(/{{employee_address}}/g, employeeData.address || '[Employee Address]')
        .replace(/{{join_date}}/g, employeeData.join_date || employeeData.joining_date || '[Joining Date]')
        .replace(/{{salary}}/g, employeeData.salary || '[Salary]')
        .replace(/{{manager_name}}/g, employeeData.manager_name || employeeData.reporting_manager || '[Manager Name]')
        .replace(/{{location}}/g, employeeData.location || employeeData.work_location || '[Location]')
        .replace(/{{probation_months}}/g, employeeData.probation_months || employeeData.probation_period || '3')
        .replace(/{{notice_period}}/g, employeeData.notice_period || '30');

    // Replace company placeholders
    const companyLogoHtml = companyData.logo || companyData.company_logo
        ? `<img src="${companyData.logo || companyData.company_logo}" alt="Company Logo" style="max-width: 200px; height: auto; margin: 10px 0;" />`
        : '[Company Logo]';

    processedContent = processedContent
        .replace(/{{company_logo}}/g, companyLogoHtml)
        .replace(/{{company_name}}/g, companyData.name || companyData.company_name || '[Company Name]')
        .replace(/{{company_address}}/g, companyData.address || companyData.company_address || '[Company Address]')
        .replace(/{{company_email}}/g, companyData.email || companyData.company_email || '[Company Email]')
        .replace(/{{company_phone}}/g, companyData.phone || companyData.company_phone || '[Company Phone]')
        .replace(/{{company_website}}/g, companyData.website || '[Company Website]')
        .replace(/{{hr_name}}/g, companyData.hr_name || '[HR Name]')
        .replace(/{{hr_title}}/g, companyData.hr_title || 'HR Manager')
        .replace(/{{ceo_name}}/g, companyData.ceo_name || '[CEO Name]');

    // Replace date placeholders
    const now = new Date();
    const dateOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    processedContent = processedContent
        .replace(/{{current_date}}/g, now.toLocaleDateString('en-US', dateOptions))
        .replace(/{{current_month}}/g, monthNames[now.getMonth()])
        .replace(/{{current_year}}/g, now.getFullYear().toString())
        .replace(/{{current_day}}/g, dayNames[now.getDay()]);

    return processedContent;
};

/**
 * Get all placeholders as a flat array
 * @returns {Array} - Array of all placeholders
 */
export const getAllPlaceholders = () => {
    return [
        ...PLACEHOLDERS.EMPLOYEE,
        ...PLACEHOLDERS.COMPANY,
        ...PLACEHOLDERS.DATE
    ];
};

/**
 * Extract placeholders from content
 * @param {string} content - Content to search for placeholders
 * @returns {Array} - Array of found placeholders
 */
export const extractPlaceholders = (content) => {
    if (!content) return [];

    const regex = /{{([^}]+)}}/g;
    const matches = [];
    let match;

    while ((match = regex.exec(content)) !== null) {
        if (!matches.includes(match[1])) {
            matches.push(match[1]);
        }
    }

    return matches;
};
